import React, { useState } from 'react';

/**
 * MediaDisplay Component
 *
 * Renders either an image or video element based on the media type.
 * Automatically handles video playback (autoplay, loop, muted) and fallback on error.
 *
 * @param {Object} media - Media object with desktop_url, mobile_url, and media_type
 * @param {boolean} isMobile - Whether to use mobile_url (optional, defaults to desktop_url)
 * @param {Object} style - CSS styles to apply to the element
 * @param {string} alt - Alt text for accessibility
 * @param {boolean} priority - If true, loads eagerly (for above-the-fold content). Default: false (lazy load)
 */
const MediaDisplay = ({ media, isMobile = false, style, alt, priority = false }) => {
  const [videoError, setVideoError] = useState(false);
  const fallbackImage = '/images/home_page/fall_2025.jpg';

  if (!media) return null;

  // Select URL based on device type
  const url = isMobile ? media.mobile_url : media.desktop_url;

  // Detect if media is video (check both media_type field and file extension)
  const isVideo = media.media_type === 'video' || url?.match(/\.(mp4|webm)$/i);

  // If video failed to load, show fallback image
  if (isVideo && videoError) {
    return <img src={fallbackImage} alt="Content unavailable" style={style} />;
  }

  // Render video element with lazy loading
  if (isVideo) {
    return (
      <video
        src={url}
        style={style}
        autoPlay
        loop
        muted
        playsInline
        preload={priority ? "auto" : "none"}
        onError={() => setVideoError(true)}
      />
    );
  }

  // Render image element with lazy loading
  // CSS in style prop handles sizing, browser uses intrinsic aspect ratio
  return (
    <img
      src={url}
      alt={alt || media.alt_text}
      style={style}
      loading={priority ? "eager" : "lazy"}
    />
  );
};

export default MediaDisplay;
